import React, {useState, useEffect, useRef} from 'react';
import usePopupAnimation from '../utils/usePopupAnimation.js';
import MultiMuscleBadge, {getWorkoutMusclesMap} from './MultiMuscleBadge.js';

function normalizeSearchValue(value) {
	return String(value || '')
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.toLowerCase()
		.trim();
}

function formatWorkoutName(item) {
	const recOrder = parseInt(item.REC_ORDER, 10);

	if (Number.isInteger(recOrder) && recOrder > 0) {
		return `${recOrder}. ${item.NAME}`;
	}

	return item.NAME;
}

export default function ExercisePlan({showPlan, closePlan, data, updatePlan, targetUsrName}) {
	const popupAnimationMs = 300;
	const [dataNew, setDataNew] = useState([]);
	const [searchTerm, setSearchTerm] = useState('');
	const [activeMuscleFilter, setActiveMuscleFilter] = useState(null);
	const searchInputRef = useRef(null);
	const {shouldRender, isClosing} = usePopupAnimation(showPlan, popupAnimationMs);

	useEffect(() => {
		setDataNew(Array.isArray(data) ? data : []);
		setSearchTerm('');
		setActiveMuscleFilter(null);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [showPlan, data]);

	const handleSubmit = (e) => {
		e.preventDefault();
		closePlan();
		updatePlan(dataNew);
	};

	const selWorkout = (e, rec) => {
		e.preventDefault();

		//--- Cvik ve více partiích je v seznamu vícekrát – zaškrtnutí platí pro všechny jeho řádky
		const newValue = rec.ISCHECKED > 0 ? 0 : 1;

		setDataNew((prevRows) => {
			const updatedRows = prevRows.map((item) => {
				if (item.ID === rec.ID) {
					return {...item, ISCHECKED: newValue};
				}

				return item;
			});

			return updatedRows;
		});
	};

	const clearSearch = () => {
		setSearchTerm('');
		searchInputRef.current?.focus();
	};

	const toggleMuscleFilter = (muscleId) => {
		setActiveMuscleFilter((prev) => (prev === muscleId ? null : muscleId));
	};

	const normalizedSearch = normalizeSearchValue(searchTerm);
	const safeData = Array.isArray(dataNew) ? dataNew : [];

	// First filter by search term only to count items for summary cards
	const searchFilteredData = safeData.filter((item) => {
		if (!normalizedSearch) {
			return true;
		}

		return normalizeSearchValue(item.NAME).includes(normalizedSearch) || normalizeSearchValue(item.MUSCLE_NAME).includes(normalizedSearch);
	});

	// Get muscle group counts based on search filtered data (total and selected)
	const muscleGroupCounts = searchFilteredData.reduce((accumulator, item) => {
		if (!accumulator[item.MUSCLEID]) {
			accumulator[item.MUSCLEID] = {total: 0, selected: 0};
		}
		accumulator[item.MUSCLEID].total += 1;
		if (item.ISCHECKED > 0) {
			accumulator[item.MUSCLEID].selected += 1;
		}
		return accumulator;
	}, {});

	// Now filter by muscle group too for list rendering
	const filteredData = searchFilteredData.filter((item) => {
		return !activeMuscleFilter || item.MUSCLEID === activeMuscleFilter;
	});

	// Exercise in more muscle groups has more rows – count unique exercises
	const filteredWorkoutCnt = new Set(filteredData.map((item) => item.ID)).size;
	const totalWorkoutCnt = new Set(safeData.map((item) => item.ID)).size;
	const workoutMusclesMap = getWorkoutMusclesMap(safeData);

	// Unique list of muscle groups based on original data (preserves order)
	const muscleGroups = [];
	const seenMuscles = new Set();
	safeData.forEach((item) => {
		if (item.MUSCLEID && !seenMuscles.has(item.MUSCLEID)) {
			seenMuscles.add(item.MUSCLEID);
			muscleGroups.push({
				id: item.MUSCLEID,
				name: item.MUSCLE_NAME,
				color: item.COLOR,
			});
		}
	});

	const groupCounts = filteredData.reduce((accumulator, item) => {
		if (!accumulator[item.MUSCLEID]) {
			accumulator[item.MUSCLEID] = {total: 0, selected: 0};
		}
		accumulator[item.MUSCLEID].total += 1;
		if (item.ISCHECKED > 0) {
			accumulator[item.MUSCLEID].selected += 1;
		}
		return accumulator;
	}, {});

	// Toggle all currently visible exercises of the group (select all, or unselect all when already fully selected)
	const selGroup = (e, muscleId) => {
		e.preventDefault();

		const groupIds = new Set(filteredData.filter((item) => item.MUSCLEID === muscleId).map((item) => item.ID));
		const stats = groupCounts[muscleId];

		if (!stats || groupIds.size === 0) {
			return;
		}

		const newValue = stats.selected === stats.total ? 0 : 1;

		setDataNew((prevRows) => prevRows.map((item) => (groupIds.has(item.ID) ? {...item, ISCHECKED: newValue} : item)));
	};

	let list = [];
	let oldRec = 0;

	filteredData?.forEach((item) => {
		const groupStats = groupCounts[item.MUSCLEID] || {total: 0, selected: 0};
		const groupCheckState = groupStats.selected === 0 ? '' : groupStats.selected === groupStats.total ? ' is-selected' : ' is-partial';

		list.push(
			<div key={'header_main' + item.ID + '_' + item.MUSCLEID} className="exercise-plan-popup__group-wrap">
				{item.MUSCLEID !== oldRec ? (
					<div key={'header_' + item.ID + '_' + item.MUSCLEID} className="exercise-plan-popup__group-head-wrap">
						{oldRec !== 0 ? <div className="zero h1" /> : ''}
						<div className="exercise-plan-popup__group-head" key={'header_in_' + item.ID + '_' + item.MUSCLEID} style={{'--exercise-plan-accent': item.COLOR}}>
							<button
								type="button"
								className="exercise-plan-popup__group-head-main"
								onClick={(e) => selGroup(e, item.MUSCLEID)}
								aria-pressed={groupCheckState === ' is-selected' ? 'true' : groupCheckState === ' is-partial' ? 'mixed' : 'false'}
							>
								<span className={`exercise-plan-popup__check${groupCheckState}`}></span>
								<span className="exercise-plan-popup__group-title">{item.MUSCLE_NAME}</span>
							</button>
							<div className="exercise-plan-popup__group-total">{groupCounts[item.MUSCLEID] ? `${groupCounts[item.MUSCLEID].selected} / ${groupCounts[item.MUSCLEID].total}` : '0/0'}</div>
						</div>
					</div>
				) : (
					''
				)}

				<button type="button" className={`exercise-plan-popup__item${item.ISCHECKED > 0 ? ' is-selected' : ''}`} key={'item_' + item.ID + '_' + item.MUSCLEID} onClick={(e) => selWorkout(e, item)}>
					<span className={`exercise-plan-popup__check${item.ISCHECKED > 0 ? ' is-selected' : ''}`}></span>
					<span className="exercise-plan-popup__item-name">
						{formatWorkoutName(item)}
						<MultiMuscleBadge muscles={workoutMusclesMap[item.ID]} currentMuscleId={item.MUSCLEID} />
					</span>
				</button>
			</div>,
		);

		oldRec = item.MUSCLEID;
	});

	return (
		<>
			{shouldRender ? (
				<div className={`popup exercise-plan-popup${isClosing ? ' popup--closing' : ''}`}>
					<div className={`popup_content exercise-plan-popup__content${isClosing ? ' popup_content--closing' : ''}`}>
						<div className="popup_content_in_head">
							<div className="popup_content_in_head_in">Plán</div>
							<img
								alt=""
								className="popup_content_in_head_in-close"
								onClick={() => {
									closePlan();
								}}
							/>
						</div>
						<div className="popup_content_in_body popup_content_in_body--hidden-scrollbar exercise-plan-popup__body">
							<div className="popup_content_in_body_in exercise-plan-popup__body-in">
								<div className="exercise-plan-popup__top">
									<div className="exercise-plan-popup__search-wrap">
										<div className="exercise-plan-popup__search-input-wrap">
											<input
												ref={searchInputRef}
												type="text"
												className="exercise-plan-popup__search"
												value={searchTerm}
												onChange={(event) => {
													setSearchTerm(event.target.value);
												}}
												placeholder="Hledat cvik nebo svalovou partii"
											/>
											{searchTerm !== '' ? <button type="button" className="exercise-plan-popup__search-clear" onClick={clearSearch} aria-label="Smazat vyhledávání" /> : ''}
										</div>
										<div className="exercise-plan-popup__search-meta">
											{filteredWorkoutCnt} / {totalWorkoutCnt} cviků
										</div>
									</div>
									{muscleGroups.length > 0 && (
										<div className="exercise-plan-popup__summary-panel">
											<div className="exercise-plan-popup__summary-grid">
												{muscleGroups.map((muscle) => {
													const isActive = activeMuscleFilter === muscle.id;
													const stats = muscleGroupCounts[muscle.id] || {total: 0, selected: 0};

													return (
														<button
															className={`home-summary-card exercise-plan-popup__summary-card${isActive ? ' is-active' : ''}`}
															key={muscle.id}
															type="button"
															aria-pressed={isActive}
															onClick={() => toggleMuscleFilter(muscle.id)}
															style={{
																'--exercise-plan-accent': muscle.color,
															}}
														>
															<div className="home-summary-card__content">
																<span className="home-summary-card__eyebrow">{muscle.name}</span>
																<span className="home-summary-card__value">
																	{stats.selected} / {stats.total}
																</span>
															</div>
														</button>
													);
												})}
											</div>
										</div>
									)}
								</div>
								<form
									method="POST"
									className="exercise-plan-popup__form popup_content_in_body--hidden-scrollbar"
									onSubmit={() => {
										return false;
									}}
								>
									{list.length > 0 ? list : <div className="exercise-plan-popup__empty">Pro zadaný výraz nebyl nalezen žádný cvik.</div>}
								</form>
								<div className="zero h0" />
							</div>
						</div>
						<div className="popup_content_in_foot exercise-plan-popup__foot">
							<div className="popup_content_in_foot_in exercise-plan-popup__foot-in">
								<input type="button" name="button_save" value="Uložit" className="button green exercise-plan-popup__save mr_2" onClick={handleSubmit} />
								<button
									type="button"
									className="button exercise-plan-popup__back"
									onClick={() => {
										closePlan();
									}}
								>
									Zpět
								</button>
							</div>
						</div>
					</div>
				</div>
			) : (
				''
			)}
		</>
	);
}
